import React from 'react';
import { EyeIcon, CheckCircleIcon, AlertTriangleIcon, MergeIcon, UndoIcon, XIcon } from 'lucide-react';
import { CustomerRecord } from './DuplicateCard';
type FieldSelection = {
  [fieldName: string]: 'left' | 'right' | null;
};
type MergePreviewProps = {
  leftRecord: CustomerRecord;
  rightRecord: CustomerRecord;
  fieldSelections: FieldSelection;
  onMerge: () => void;
  onCancel: () => void;
  isVisible: boolean;
  mergeStatus: 'idle' | 'merging' | 'success' | 'error';
  onUndo?: () => void;
};
export function MergePreview({
  leftRecord,
  rightRecord,
  fieldSelections,
  onMerge,
  onCancel,
  isVisible,
  mergeStatus,
  onUndo
}: MergePreviewProps) {
  if (!isVisible) return null;
  const getMergedValue = (fieldName: keyof CustomerRecord): string => {
    const selection = fieldSelections[fieldName];
    if (selection === 'left') return String(leftRecord[fieldName]);
    if (selection === 'right') return String(rightRecord[fieldName]);
    return String(leftRecord[fieldName]);
  };
  const unresolvedConflicts = Object.entries(fieldSelections).filter(([fieldName, selection]) => {
    const leftVal = String(leftRecord[fieldName as keyof CustomerRecord]);
    const rightVal = String(rightRecord[fieldName as keyof CustomerRecord]);
    return leftVal !== rightVal && selection === null;
  }).length;
  const formatCurrency = (amount: string) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0
    }).format(Number(amount));
  };
  if (mergeStatus === 'success') {
    return <div className="fixed bottom-0 left-0 right-0 bg-green-50 border-t-2 border-green-500 p-6 shadow-2xl z-20" data-id="element-149">
        <div className="max-w-5xl mx-auto flex items-center justify-between" data-id="element-150">
          <div className="flex items-center gap-4" data-id="element-151">
            <div className="flex-shrink-0 w-12 h-12 bg-green-100 rounded-full flex items-center justify-center" data-id="element-152">
              <CheckCircleIcon className="w-6 h-6 text-green-600" aria-hidden="true" data-id="element-153" />
            </div>
            <div data-id="element-154">
              <h3 className="text-base font-semibold text-green-900" data-id="element-155">
                Records Merged Successfully!
              </h3>
              <p className="text-sm text-green-700" data-id="element-156">
                The duplicate records have been combined into a single record.
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3" data-id="element-157">
            {onUndo && <button onClick={onUndo} className="flex items-center gap-2 px-4 py-2 bg-white border-2 border-green-300 text-green-700 rounded-lg hover:bg-green-50 transition-colors focus:outline-none focus:ring-2 focus:ring-green-500 font-medium text-sm" data-id="element-158">
                <UndoIcon className="w-4 h-4" aria-hidden="true" data-id="element-159" />
                Undo Merge
              </button>}
            <button onClick={onCancel} className="p-2 text-green-700 hover:text-green-900 hover:bg-green-100 rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-green-500" aria-label="Close success message" data-id="element-160">
              <XIcon className="w-5 h-5" aria-hidden="true" data-id="element-161" />
            </button>
          </div>
        </div>
      </div>;
  }
  return <div className="fixed bottom-0 left-0 right-0 bg-white border-t-2 border-blue-500 shadow-2xl z-20" data-id="element-162">
      {/* Header */}
      <div className="bg-blue-50 px-6 py-4 flex items-center justify-between border-b border-blue-100" data-id="element-163">
        <div className="flex items-center gap-3" data-id="element-164">
          <EyeIcon className="w-5 h-5 text-blue-600" aria-hidden="true" data-id="element-165" />
          <h3 className="text-base font-semibold text-blue-900" data-id="element-166">
            Merge Preview
          </h3>
          {unresolvedConflicts > 0 && <span className="flex items-center gap-1.5 text-sm text-yellow-800 bg-yellow-100 px-3 py-1 rounded-full border border-yellow-200 font-medium" data-id="element-167">
              <AlertTriangleIcon className="w-4 h-4" aria-hidden="true" data-id="element-168" />
              {unresolvedConflicts} unresolved
            </span>}
        </div>
        <button onClick={onCancel} className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500" aria-label="Close preview" data-id="element-169">
          <XIcon className="w-5 h-5" aria-hidden="true" data-id="element-170" />
        </button>
      </div>

      {/* Preview Content */}
      <div className="p-6" data-id="element-171">
        <div className="max-w-5xl mx-auto" data-id="element-172">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6" data-id="element-173">
            <div className="bg-gray-50 rounded-lg p-4 border border-gray-200" data-id="element-174">
              <p className="text-xs font-medium text-gray-500 mb-1.5" data-id="element-175">Name</p>
              <p className="text-sm font-semibold text-gray-900 truncate" data-id="element-176">
                {getMergedValue('firstName')} {getMergedValue('lastName')}
              </p>
            </div>
            <div className="bg-gray-50 rounded-lg p-4 border border-gray-200" data-id="element-177">
              <p className="text-xs font-medium text-gray-500 mb-1.5" data-id="element-178">Email</p>
              <p className="text-sm font-semibold text-gray-900 truncate" data-id="element-179">
                {getMergedValue('email')}
              </p>
            </div>
            <div className="bg-gray-50 rounded-lg p-4 border border-gray-200" data-id="element-180">
              <p className="text-xs font-medium text-gray-500 mb-1.5" data-id="element-181">Phone</p>
              <p className="text-sm font-semibold text-gray-900" data-id="element-182">
                {getMergedValue('phone')}
              </p>
            </div>
            <div className="bg-gray-50 rounded-lg p-4 border border-gray-200" data-id="element-183">
              <p className="text-xs font-medium text-gray-500 mb-1.5" data-id="element-184">
                Loan Amount
              </p>
              <p className="text-sm font-semibold text-gray-900" data-id="element-185">
                {formatCurrency(getMergedValue('loanAmount'))}
              </p>
            </div>
          </div>

          {/* Actions */}
          <div className="flex items-center justify-between pt-4 border-t border-gray-200" data-id="element-186">
            <p className="text-sm text-gray-600" data-id="element-187">
              This will merge Record B into Record A. Record B will be archived.
            </p>
            <div className="flex items-center gap-3" data-id="element-188">
              <button onClick={onCancel} className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border-2 border-gray-300 rounded-lg hover:bg-gray-50 transition-colors focus:outline-none focus:ring-2 focus:ring-gray-400" data-id="element-189">
                Cancel
              </button>
              <button onClick={onMerge} disabled={unresolvedConflicts > 0 || mergeStatus === 'merging'} className={`flex items-center gap-2 px-6 py-2 rounded-lg text-sm font-semibold transition-all focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${unresolvedConflicts > 0 ? 'bg-gray-200 text-gray-400 cursor-not-allowed border-2 border-gray-300' : 'bg-blue-600 text-white hover:bg-blue-700 shadow-sm hover:shadow-md'}`} aria-disabled={unresolvedConflicts > 0} data-id="element-190">
                {mergeStatus === 'merging' ? <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" aria-hidden="true" data-id="element-191" />
                    Merging...
                  </> : <>
                    <MergeIcon className="w-4 h-4" aria-hidden="true" data-id="element-192" />
                    Merge Records
                  </>}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>;
}